"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { signIn } from "@/lib/auth-client";
import { ArrowRightIcon } from "@/components/icons/phosphor";

// The netzstrategen rainbow sweep plays at 2× speed (see playbackRate below), so
// it's visually complete by ~1.15s; we hand off to Keycloak a hair earlier so the
// redirect overlaps the tail and feels snappy.
const RAINBOW_HANDOFF_MS = 1000;

export default function LoginPage() {
  const t = useTranslations("Login");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [playing, setPlaying] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A failed/aborted login must never leave a late redirect armed.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // The real redirect. On success the browser leaves for Keycloak; we only
  // return here on error, in which case we tear the overlay back down.
  async function doLogin() {
    setPending(true);
    const { error } = await signIn.oauth2({ providerId: "keycloak", callbackURL: "/" });
    if (error) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setPending(false);
      setPlaying(false);
      setError(t("authError"));
    }
  }

  function onLogin() {
    setError(null);

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      void doLogin();
      return;
    }

    setPlaying(true);

    // Some browsers only honour playback started inside the user-gesture
    // handler. If autoplay is blocked, skip the flourish and log in now.
    const v = videoRef.current;
    const play = v?.play();
    if (play && typeof play.catch === "function") {
      play.catch(() => {
        setPlaying(false);
        void doLogin();
      });
    }

    timerRef.current = setTimeout(() => void doLogin(), RAINBOW_HANDOFF_MS);
  }

  return (
    <main className="login-root">
      <div className="login-hero">
        <Image
          className="login-logo"
          src="/netzstrategen-logo.png"
          alt="netzstrategen"
          width={710}
          height={168}
          priority
        />

        <h1 className="login-claim">
          We make AI work.
        </h1>

        <p className="login-subline">
          AI creates opportunities. Strategy creates impact.
        </p>

        <div className="login-card">
          {error && <div className="login-error">{error}</div>}

          <button
            type="button"
            className="login-btn"
            onClick={onLogin}
            disabled={pending || playing}
          >
            {pending ? t("redirecting") : t("signIn")}
            <span className="login-btn-arrow">
              <ArrowRightIcon size={16} />
            </span>
          </button>
        </div>
      </div>

      <div className="login-bottombar" aria-hidden="true" />

      {/* Warm the bytes so the click-to-rainbow is instant. */}
      <video
        ref={videoRef}
        className="login-preload"
        src="/netz-rainbow.mp4"
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
      />

      {playing && (
        <div className="login-rainbow" aria-hidden="true">
          {/* Promote the warmed preload element into the full-screen takeover. */}
          <video
            className="login-rainbow-video"
            src="/netz-rainbow.mp4"
            autoPlay
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => {
              // Play at 2× so the sweep runs half as long, matching RAINBOW_HANDOFF_MS.
              e.currentTarget.playbackRate = 2;
            }}
          />
        </div>
      )}
    </main>
  );
}
